import React, { useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Descriptions, Badge, Button, Space, Card, Typography, message, Table } from "antd";
import { Briefcase, X, User, Mail } from "react-feather";
import { useQuery, useQueryClient } from "react-query";
import { api_srv } from "services";

const { Title, Text } = Typography;

export default function ViewInvoice() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isMounted = useRef(true);
  const history = useHistory();

  const viewQuotation = (id)  => history.push(`/dashboard/direct-quotation/${id}`);

  const get_invoice_details = async () => {
    let resp = await (await api_srv).fetchInvoiceDetails(id);
    console.log("res invoice details---", resp);
    return resp.data;
  };

  const {
    data: invoiceData,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["invoices_details", id],
    queryFn: get_invoice_details,
    staleTime: 5 * 60 * 1000,
    retry: 3,
    onSettled: () => {
      // Ensure no state updates occur if the component is unmounted
      if (!isMounted.current) return;
    },
  });

  useEffect(() => {
    console.log(`Fetching invoice details for ID: ${id}`);
    
    // Cleanup function to cancel queries and prevent state updates
    return () => {
      isMounted.current = false;
      queryClient.cancelQueries(["invoices_details", id]);
    };
  }, [id, queryClient]);

  if (isLoading || isFetching) {
    return <div>Loading invoices...</div>;
  }

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  if (!invoiceData || !Array.isArray(invoiceData) || invoiceData.length === 0) {
    return <div>No invoice data found</div>;
  }

  const invoice = invoiceData[0]; // Access the first (and only) object in the array

  const getStatusColor = (status) => {
    const statusColors = {
      'pending-payment': '#faad14',
      paid: '#52c41a',
      overdue: '#ff4d4f',
      cancelled: '#ff4d4f',
    };
    return statusColors[status?.toLowerCase()] || '#52c41a';
  };

  const handlePay = async () => {
    try {
      await (await api_srv).updateInvoiceStatus(id, 'PAID');
      message.success('Payment processed successfully');
      queryClient.invalidateQueries(["invoices_details", id]); // Refresh query
    } catch (error) {
      message.error('Failed to process payment');
    }
  };

  const handleCancel = async () => {
    try {
      await (await api_srv).updateInvoiceStatus(id, 'CANCELLED');
      message.success('Invoice cancelled');
      queryClient.invalidateQueries(["invoices_details", id]); // Refresh query
    } catch (error) {
      message.error('Failed to cancel invoice');
    }
  };

  // Table columns for items
  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value) => `${invoice.invoiceCurrency} ${value.toLocaleString()}`,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
      <Card 
        style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: 'none' }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>Invoice</Text>
              <Title level={3} style={{ margin: 0, color: '#1890ff', fontWeight: 600 }}>
                {invoice.invoiceCategory}
              </Title>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Badge 
                color={getStatusColor(invoice.invoiceStatus)} 
                text={invoice.invoiceStatus.toUpperCase()}
                style={{ fontSize: '12px', fontWeight: '600' }}
              />
              <Space>
                <Button 
                  type="primary" 
                  onClick={handlePay}
                  disabled={invoice.invoiceStatus === 'PAID' || invoice.invoiceStatus === 'CANCELLED'}
                >
                  Pay Now
                </Button>
                <Button 
                  type="default" 
                  icon={<X size={16} />}
                  onClick={handleCancel}
                  disabled={invoice.invoiceStatus === 'CANCELLED'}
                >
                  Cancel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'column', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '8px' }}>
              Invoice Details
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              System Ref: {invoice.invoiceSystemRef}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              {/* Category: {invoice.invoiceCategory} */}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Remarks: {invoice.remarks}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <Text strong>Items:</Text>
            <Table
              columns={columns}
              dataSource={invoice.items}
              pagination={false}
              rowKey="description"
              style={{ marginTop: '8px' }}
            />
          </div>
          <div style={{ textAlign: 'right', flex: 1 }}>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Created: {new Date(invoice.createdDate).toLocaleDateString()}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
              Due: {new Date(Number(invoice.expectedPaymentDate)).toLocaleDateString()}
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#059669' }}>
              {invoice.invoiceCurrency} {invoice.invoiceAmount.toLocaleString()}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              Total Amount
            </div>
          </div>
        </div>
      </Card>

      <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
          <User size={20} style={{ color: '#1890ff' }} />
          <Title level={4} style={{ margin: 0, color: '#1f2937' }}>Billing Information</Title>
        </div>
        <Descriptions 
          bordered 
          column={2}
          size="middle"
          labelStyle={{ width: '20%', fontWeight: 500, background: '#fafafa' }}
          contentStyle={{ background: '#fff' }}
          style={{ borderRadius: '8px' }}
        >
          <Descriptions.Item label="From Entity">
            <span>{invoice.from.entityType} </span>
          </Descriptions.Item>
          {/* <Descriptions.Item label="To Entity">
            <span>{invoice.to.entityType} (ID: {invoice.to.entityID})</span>
          </Descriptions.Item> */}
          <Descriptions.Item label="Amount">
            <span>{invoice.invoiceCurrency} {invoice.invoiceAmount.toLocaleString()}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Badge 
              color={getStatusColor(invoice.invoiceStatus)} 
              text={invoice.invoiceStatus.toUpperCase()}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Created Date">
            <span>{new Date(invoice.createdDate).toLocaleDateString()}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Due Date">
            <span>{new Date(Number(invoice.expectedPaymentDate)).toLocaleDateString()}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Category">
            <span>{invoice.invoiceCategory}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Created By">
            <span>{invoice.createdBy}</span>
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
}